/**
 * Reconnecting WebSocket with app-level liveness detection.
 * - exponential backoff 0.5 s → 5 s (+ jitter)
 * - immediate retry on `online` / page becoming visible
 * - connection considered dead after 40 s without any server message
 */
import {
  parseServerMessage,
  safeJsonParse,
  type ClientMessage,
  type ServerMessage,
} from '../../shared/protocol.js';
import type { Transport } from './sync.js';

export interface SocketHandlers {
  onConnecting(): void;
  onOpen(): void;
  onMessage(msg: ServerMessage): void;
  onClose(): void;
}

export interface ReconnectingSocket extends Transport {
  /** Retry immediately if not connected (used on online/visibility events). */
  reconnectNow(): void;
  /** Drop the current connection and reconnect at once (e.g. after a new PIN). */
  restart(): void;
  close(): void;
}

export const CLOSE_BAD_ROOM = 4001;
export const CLOSE_BAD_PIN = 4003;
export const CLOSE_RATE_LIMITED = 4029;

const STALE_MS = 40_000;
const MAX_BACKOFF_MS = 5_000;

export function createSocket(url: string, handlers: SocketHandlers): ReconnectingSocket {
  let ws: WebSocket | null = null;
  let attempts = 0;
  let stopped = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let staleTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (staleTimer) clearTimeout(staleTimer);
    reconnectTimer = null;
    staleTimer = null;
  };

  const armStale = () => {
    if (staleTimer) clearTimeout(staleTimer);
    staleTimer = setTimeout(() => {
      console.warn('[socket] no message for 40 s, reconnecting');
      ws?.close();
    }, STALE_MS);
  };

  const scheduleReconnect = (delayMs?: number) => {
    if (stopped || reconnectTimer) return;
    const backoff = Math.min(MAX_BACKOFF_MS, 500 * 2 ** attempts) + Math.random() * 250;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delayMs ?? backoff);
    attempts += 1;
  };

  const connect = () => {
    if (stopped || ws) return;
    handlers.onConnecting();
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      console.warn('[socket] cannot create WebSocket', err);
      scheduleReconnect();
      return;
    }
    ws = socket;

    socket.onopen = () => {
      attempts = 0;
      armStale();
      handlers.onOpen();
    };
    socket.onmessage = (ev) => {
      armStale();
      const msg = parseServerMessage(safeJsonParse(String(ev.data)));
      if (msg === null) {
        console.warn('[socket] dropped invalid server message');
        return;
      }
      handlers.onMessage(msg);
    };
    socket.onerror = () => {
      /* onclose follows; nothing to do */
    };
    socket.onclose = (ev) => {
      if (ws !== socket) return;
      ws = null;
      if (staleTimer) clearTimeout(staleTimer);
      handlers.onClose();
      if (ev.code === CLOSE_BAD_ROOM) {
        stopped = true; // wrong room token: retrying is pointless
        return;
      }
      if (ev.code === CLOSE_RATE_LIMITED) scheduleReconnect(60_000);
      else scheduleReconnect();
    };
  };

  const reconnectNow = () => {
    if (stopped) return;
    // Open or still connecting/closing: leave it alone (a dead OPEN socket is
    // caught by the stale detector, a CONNECTING one by its own timeout).
    if (ws) return;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    attempts = 0;
    connect();
  };

  const restart = () => {
    if (stopped) return;
    attempts = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (ws) ws.close();
    else connect();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', reconnectNow);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reconnectNow();
    });
    window.addEventListener('pageshow', reconnectNow);
  }

  connect();

  return {
    send(msg: ClientMessage): boolean {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify(msg));
        return true;
      } catch (err) {
        console.warn('[socket] send failed', err);
        return false;
      }
    },
    isOpen: () => ws !== null && ws.readyState === WebSocket.OPEN,
    reconnectNow,
    restart,
    close() {
      stopped = true;
      clearTimers();
      ws?.close();
      ws = null;
    },
  };
}
